import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
  LayoutDashboard,
  Upload,
  FileSearch,
  History,
  MessageSquareMore,
  Settings,
  ChevronLeft,
  ChevronRight,
  Search,
  Clock,
  MoreHorizontal,
  Pencil,
  Star,
  Archive,
  Trash2,
} from 'lucide-react';
import { ReactComponent as TraceLogo } from '../../assets/trace-logo.svg';
import { ReactComponent as TraceFevicon } from '../../assets/trace-fevicon.svg';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'upload', label: 'Upload Document', icon: Upload },
  { id: 'analysis', label: 'Analysis', icon: FileSearch },
  { id: 'documents', label: 'Documents', icon: History },
  { id: 'feedback', label: 'Feedback', icon: MessageSquareMore },
  { id: 'settings', label: 'Settings', icon: Settings },
];

/* ── helpers ──────────────────────────────────────────────────── */
function groupByDate<T extends { uploadDate: string }>(items: T[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups: { label: string; items: T[] }[] = [
    { label: 'Today', items: [] },
    { label: 'Yesterday', items: [] },
    { label: 'Earlier', items: [] },
  ];

  for (const item of items) {
    const d = new Date(item.uploadDate);
    if (d >= today) groups[0].items.push(item);
    else if (d >= yesterday) groups[1].items.push(item);
    else groups[2].items.push(item);
  }
  return groups.filter(g => g.items.length > 0);
}

/* ── context menu ─────────────────────────────────────────────── */
interface MenuProps {
  x: number;
  y: number;
  docId: string;
  docName: string;
  isBookmarked: boolean;
  onClose: () => void;
}

const ContextMenu: React.FC<MenuProps> = ({ x, y, docId, docName, isBookmarked, onClose }) => {
  const { deleteDocument, renameDocument, toggleBookmark, archiveDocument } = useApp();
  const ref = useRef<HTMLDivElement>(null);
  const [renaming, setRenaming] = useState(false);
  const [nameValue, setNameValue] = useState(docName);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const menuItems = [
    {
      icon: Pencil, label: 'Rename', onClick: () => setRenaming(true),
    },
    {
      icon: Star,
      label: isBookmarked ? 'Remove Bookmark' : 'Bookmark',
      onClick: () => { toggleBookmark(docId); onClose(); },
    },
    {
      icon: Archive, label: 'Archive', onClick: () => { archiveDocument(docId); onClose(); },
    },
    {
      icon: Trash2, label: 'Delete', danger: true,
      onClick: () => setConfirmDelete(true),
    },
  ];

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[180px] bg-white rounded-xl border border-surface-200
        shadow-lg shadow-surface-900/10 py-1.5 animate-in fade-in slide-in-from-top-1 duration-150"
      style={{ top: y, left: x }}
    >
      {renaming ? (
        <form
          className="px-3 py-2"
          onSubmit={e => { e.preventDefault(); renameDocument(docId, nameValue.trim() || docName); onClose(); }}
        >
          <input
            autoFocus
            value={nameValue}
            onChange={e => setNameValue(e.target.value)}
            className="w-full px-2 py-1.5 text-xs rounded-lg border border-brand-300 bg-brand-50/50
              focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
          <div className="flex gap-1.5 mt-2">
            <button type="submit" className="flex-1 text-[11px] font-medium py-1 rounded-lg bg-brand-400 text-white hover:bg-brand-500 transition-colors">
              Save
            </button>
            <button type="button" onClick={onClose} className="flex-1 text-[11px] font-medium py-1 rounded-lg bg-surface-100 text-surface-600 hover:bg-surface-200 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      ) : confirmDelete ? (
        <div className="px-3 py-2">
          <p className="text-xs text-surface-600 mb-2">Delete <strong>{docName}</strong>?</p>
          <div className="flex gap-1.5">
            <button onClick={() => { deleteDocument(docId); onClose(); }} className="flex-1 text-[11px] font-medium py-1 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors">
              Delete
            </button>
            <button onClick={onClose} className="flex-1 text-[11px] font-medium py-1 rounded-lg bg-surface-100 text-surface-600 hover:bg-surface-200 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        menuItems.map(item => (
          <button
            key={item.label}
            onClick={item.onClick}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors
              ${item.danger
                ? 'text-red-500 hover:bg-red-50'
                : 'text-surface-600 hover:bg-surface-50 hover:text-surface-800'}`}
          >
            <item.icon size={14} />
            <span>{item.label}</span>
          </button>
        ))
      )}
    </div>
  );
};

/* ── sidebar ──────────────────────────────────────────────────── */
export const Sidebar: React.FC = () => {
  const {
    activeView, setActiveView, sidebarCollapsed, setSidebarCollapsed,
    documents, navigateToDocument, selectedDocumentId,
    bookmarkedIds, archivedIds,
  } = useApp();

  const [menu, setMenu] = useState<{ x: number; y: number; docId: string; docName: string } | null>(null);

  // Filter out archived docs, sort newest first
  const visibleDocs = documents
    .filter(d => !archivedIds.has(d.id))
    .sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());

  const groups = groupByDate(visibleDocs);

  const openMenu = (e: React.MouseEvent, docId: string, docName: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenu({ x: rect.right + 4, y: rect.top, docId, docName });
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-white border-r border-surface-200/60 z-30
        flex flex-col transition-all duration-300 ease-out
        ${sidebarCollapsed ? 'w-[72px]' : 'w-[272px]'}`}
    >
      {/* Logo — click to go back to Dashboard */}
      <button
        onClick={() => setActiveView('dashboard')}
        className={`flex items-center border-b border-surface-100 px-4 w-full
          cursor-pointer hover:bg-surface-50/60 active:bg-surface-100/60
          transition-colors duration-200 ${sidebarCollapsed ? 'justify-center h-16' : 'h-20 pt-2'}`}
        title="Go to Dashboard"
      >
        {sidebarCollapsed ? (
          <TraceFevicon className="h-8 w-8" />
        ) : (
          <TraceLogo className="h-auto w-full max-h-14" />
        )}
      </button>

      {/* Search Documents - only when expanded */}
      {!sidebarCollapsed && (
        <div className="px-4 pt-4 pb-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
            <input
              type="text"
              placeholder="Search Documents"
              className="w-full pl-9 pr-3 py-2 text-xs rounded-lg bg-surface-50 border border-surface-200
                placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-brand-400/20 focus:border-brand-400/40
                transition-all"
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = activeView === item.id;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={`w-full ${isActive ? 'nav-item-active' : 'nav-item-inactive'} ${sidebarCollapsed ? 'justify-center px-0' : ''}`}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <Icon size={18} className={isActive ? 'text-brand-500' : ''} />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </button>
          );
        })}

        {/* Document History - only when expanded */}
        {!sidebarCollapsed && (
          <div className="pt-4 mt-4 border-t border-surface-100">
            <div className="flex items-center gap-2 px-3 mb-3">
              <Clock size={13} className="text-surface-400" />
              <span className="text-[11px] font-semibold text-surface-400 uppercase tracking-widest">Document History</span>
            </div>

            {groups.length === 0 && (
              <p className="text-xs text-surface-300 px-3 italic">No documents yet</p>
            )}

            {groups.map(group => (
              <div key={group.label} className="mb-3">
                <p className="text-[10px] font-semibold text-surface-300 uppercase tracking-widest px-3 mb-1.5">
                  {group.label}
                </p>
                {group.items.map((doc) => {
                  const isSelected = selectedDocumentId === doc.id;
                  const isBookmarked = bookmarkedIds.has(doc.id);
                  return (
                    <div
                      key={doc.id}
                      className={`group relative flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all cursor-pointer
                        ${isSelected
                          ? 'bg-brand-50 text-brand-600'
                          : 'text-surface-500 hover:text-surface-800 hover:bg-surface-50'}`}
                      onClick={() => navigateToDocument(doc.id)}
                      onContextMenu={(e) => openMenu(e, doc.id, doc.docName)}
                    >
                      {isBookmarked && (
                        <Star size={11} className="text-amber-400 fill-amber-400 shrink-0" />
                      )}
                      <span className="text-xs font-medium truncate flex-1" title={doc.docName}>
                        {doc.docName}
                      </span>
                      <button
                        onClick={(e) => openMenu(e, doc.id, doc.docName)}
                        className="opacity-0 group-hover:opacity-100 shrink-0 p-0.5 rounded hover:bg-surface-200/60 transition-all"
                        title="More actions"
                      >
                        <MoreHorizontal size={14} className="text-surface-400" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </nav>

      {/* Version + Collapse */}
      <div className="border-t border-surface-100 p-3">
        {!sidebarCollapsed && (
          <p className="text-[10px] text-surface-300 font-medium text-center mb-2">Version 0.0.1</p>
        )}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-surface-400
            hover:text-surface-700 hover:bg-surface-50 transition-all text-xs"
        >
          {sidebarCollapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span>Collapse</span></>}
        </button>
      </div>

      {/* Context Menu Portal */}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          docId={menu.docId}
          docName={menu.docName}
          isBookmarked={bookmarkedIds.has(menu.docId)}
          onClose={() => setMenu(null)}
        />
      )}
    </aside>
  );
};
